/** The admin SPA's notion of "which site am I editing". */
export interface SiteConfig {
  CURRENT_DOMAIN_SITE: string;
  CURRENT_SITE_ID: string;
}

// Dev-only fallback: a site that exists in the local/dev DB and that the dev
// user has rights to. NEVER silently fall back to a (production) tenant that
// does not exist locally — that hides misconfiguration and yields an empty/
// read-only screen.
export const configCurrentSiteloc: SiteConfig = {
  CURRENT_DOMAIN_SITE: "localhost:3001",
  CURRENT_SITE_ID: "5f5e9af16bf26151249d2288",
};

// Mutable singleton — starts with dev fallback, overwritten by initSiteConfig() before first render.
export const configCurrentSite: SiteConfig = {
  CURRENT_DOMAIN_SITE: configCurrentSiteloc.CURRENT_DOMAIN_SITE,
  CURRENT_SITE_ID: configCurrentSiteloc.CURRENT_SITE_ID,
};

/**
 * Resolves the current site by asking the backend which tenant this request
 * belongs to. Must be awaited before the app renders so that api.js's
 * interceptor has the correct siteId. No-op in SSR context.
 *
 * The backend answers from the request itself (server/utils/siteDomain.js):
 * the Host header on a real domain, and its own DEFAULT_SITE_ID on localhost /
 * docker-compose / e2e, where `localhost:3001` matches no site record.
 *
 * This replaces the old REACT_APP_CURRENT_SITE_ID override, which was baked
 * into the bundle at build time and `sed`-substituted at container start
 * (SCRUM-890). The site pin now lives on the backend service, which already
 * owns exactly one site per local stack.
 */
export async function initSiteConfig(): Promise<void> {
  if (typeof window === "undefined") return;

  try {
    const res = await fetch("/api/sites/current");
    if (!res.ok) {
      // A backend that cannot reach MongoDB answers 503 with the marker
      // /api/health returns (server/helpers/healthCheck.ts, SCRUM-1432). Name it,
      // because `HTTP 400` — what this line logged before — sent the 2026-09-04
      // staging outage to ECS events and CloudWatch to be diagnosed, while the
      // accurate answer was in the response body all along.
      let body: { checks?: { mongo?: string } } | null = null;
      try {
        body = await res.json();
      } catch {
        body = null;
      }
      if (body?.checks?.mongo === "disconnected") {
        throw new Error(
          `database unreachable — backend reports checks.mongo=disconnected (HTTP ${res.status})`
        );
      }
      throw new Error(`HTTP ${res.status}`);
    }

    // /api/sites/current wraps the site: { currentSite, pagesCount },
    // mirroring searchByDomain (server controllers/site.js).
    const data: { currentSite?: { _id?: string; domain?: string } } | null =
      await res.json();
    const site = data?.currentSite;
    if (site?._id) {
      configCurrentSite.CURRENT_DOMAIN_SITE =
        site.domain || window.location.hostname;
      configCurrentSite.CURRENT_SITE_ID = site._id;
    }
  } catch (err) {
    console.error("[config] Could not resolve current site:", err);
  }
}
